import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { journalPrompts } from '../data/journalPrompts';
import LegalFooter from '../components/LegalFooter';
import '../styles/coastal-dusk.css';

const InterpretationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicTrack, setMusicTrack] = useState('beach'); // 'beach' or 'flow'
  const [narrationPlaying, setNarrationPlaying] = useState(false);
  const [narrationLoading, setNarrationLoading] = useState(false);
  const [narrationLoadingMessage, setNarrationLoadingMessage] = useState('Loading...');
  const [dataRetention, setDataRetention] = useState(null);
  const [showConsentSection, setShowConsentSection] = useState(true);
  const [previousInterpretations, setPreviousInterpretations] = useState([]);
  const [showPastJourneys, setShowPastJourneys] = useState(false);
  const [selectedJourneyIndex, setSelectedJourneyIndex] = useState(-1);
  const [audioDownloading, setAudioDownloading] = useState(false);
  const [audioDownloadMessage, setAudioDownloadMessage] = useState('');
  const [hasUnusedAccess, setHasUnusedAccess] = useState(false);
  
  // Fold/unfold states for sections
  const [showInsights, setShowInsights] = useState(false);
  const [showPriorities, setShowPriorities] = useState(false);
  const [showArtifacts, setShowArtifacts] = useState(false);
  
  // Button feedback states
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  
  const musicRef = useRef(null);
  const alphaRef = useRef(null);
  const narrationRef = useRef(null);
  const hasGeneratedRef = useRef(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // IntersectionObserver to add 'visible' class to .reveal elements
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  });

  // Function to pause all audio when exit video plays
  const pauseAllAudio = useCallback(() => {
    if (musicRef.current && musicPlaying) {
      musicRef.current.pause();
      setMusicPlaying(false);
    }
    if (alphaRef.current) {
      alphaRef.current.pause();
    }
    if (narrationRef.current && narrationPlaying) {
      narrationRef.current.pause();
      setNarrationPlaying(false);
    }
  }, [musicPlaying, narrationPlaying]);

  // Handler for saving consent preferences
  const handleSaveConsent = async () => {
    try {
      setSavingPreferences(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/user/consent`,
        {
          email_consent: null,
          data_retention: dataRetention
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowConsentSection(false);
      alert('Your preferences have been saved successfully!');
    } catch (err) {
      console.error('Error saving consent:', err);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSavingPreferences(false);
    }
  };

  useEffect(() => {
    // Prevent back navigation - replace history entry multiple times to create a buffer
    window.history.pushState(null, '', window.location.href);
    window.history.pushState(null, '', window.location.href);
    
    const handleBackButton = (event) => {
      // Prevent going back by pushing state again
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      console.log('Back navigation prevented on interpretation page');
    };
    
    window.addEventListener('popstate', handleBackButton);
    
    // Check if returning user or new generation (only once)
    if (!hasGeneratedRef.current) {
      hasGeneratedRef.current = true;
      loadInterpretation();
    }
    
    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, []);

  // Removed the separate handleBackButton since it's now inside useEffect

  const loadInterpretation = async () => {
    const responses = location.state?.responses;
    
    // If we have responses from journaling, generate new interpretation
    if (responses) {
      await generateInterpretation(responses);
    } else {
      // Otherwise, load existing interpretation for returning user
      await loadExistingInterpretation();
    }
  };

  const loadExistingInterpretation = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const result = await axios.get(
        `${BACKEND_URL}/api/interpretation/mine`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = result.data.interpretation_data;
      const previousJourneys = result.data.previous_interpretations || [];
      const consentPrefs = result.data.consent_preferences;
      
      // Transform the saved data to match the expected format
      // Support both new format (narration) and legacy format (anchor + interpretation)
      const narrationText = data.narration || data.interpretation || '';
      const anchorText = data.anchor || '';
      
      // Fix journal entries to use actual prompt text from journalPrompts
      const fixedJournalEntries = (data.journal_entries || []).map((entry, index) => {
        const promptIndex = (entry.prompt_number || index + 1) - 1;
        const promptData = journalPrompts[promptIndex];
        return {
          prompt_number: entry.prompt_number || index + 1,
          prompt_text: promptData?.text || entry.prompt_text || `Prompt ${index + 1}`,
          user_response: entry.user_response || ''
        };
      });
      
      setInterpretation({
        narration: narrationText,
        anchor: anchorText,  // Keep for legacy support
        interpretation_text: data.interpretation || narrationText,  // Legacy field
        insights: data.insights || [],  // New 8-theme insights
        priorities: data.priorities || [],
        journal_entries: fixedJournalEntries,  // For PDF download - now with correct prompt text
        full_text_length: narrationText.length
      });
      
      // Set previous interpretations if any exist
      setPreviousInterpretations(previousJourneys);
      
      // Check if user has unused access (paid for new session)
      if (result.data.has_unused_access) {
        setHasUnusedAccess(true);
      }
      
      // Hide consent section if user already saved preferences
      if (consentPrefs && consentPrefs.data_retention) {
        setShowConsentSection(false);
        setDataRetention(consentPrefs.data_retention);
      }
      
      setLoading(false);
      console.log(`Loaded existing interpretation for returning user (${previousJourneys.length} previous journeys)`);
    } catch (err) {
      console.error('Error loading interpretation:', err);
      setError(err.response?.data?.detail || 'Failed to load interpretation. Please try journaling again.');
      setLoading(false);
    }
  };

  const [retryResponses, setRetryResponses] = useState(null);

  const generateInterpretation = async (responses) => {
    try {
      setRetryResponses(responses);
      // Convert responses object to array format for API
      const journalResponses = Object.entries(responses).map(([promptIndex, response]) => {
        const idx = parseInt(promptIndex);
        const promptData = journalPrompts[idx];
        return {
          prompt_number: idx + 1,
          prompt_text: promptData?.text || `Prompt ${idx + 1}`,
          user_response: response
        };
      });

      const token = localStorage.getItem('token');
      
      // Trigger generation (returns 202 immediately)
      await axios.post(
        `${BACKEND_URL}/api/interpretation/generate`,
        { journal_responses: journalResponses },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );

      // Poll for completion
      await pollGenerationStatus(token);
    } catch (err) {
      // If we get 202, start polling
      if (err.response?.status === 202) {
        const token = localStorage.getItem('token');
        await pollGenerationStatus(token);
      } else {
        console.error('Error generating interpretation:', err);
        setError(err.response?.data?.detail || 'Failed to generate interpretation');
        setLoading(false);
      }
    }
  };

  const pollGenerationStatus = async (token) => {
    const maxAttempts = 60; // 60 * 3s = 180s max
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 3000));
      try {
        const statusRes = await axios.get(
          `${BACKEND_URL}/api/interpretation/generation-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (statusRes.data.status === 'complete') {
          // Load the generated interpretation
          await loadExistingInterpretation();
          return;
        } else if (statusRes.data.status === 'error') {
          setError(statusRes.data.message || 'Generation failed. Please try again.');
          setLoading(false);
          return;
        }
        // Otherwise keep polling...
      } catch (pollErr) {
        console.error('Poll error:', pollErr);
      }
    }
    setError('Generation is taking longer than expected. Please refresh the page in a moment.');
    setLoading(false);
  };

  const handleMusicToggle = (track) => {
    const selectedTrack = track || musicTrack;
    const currentRef = selectedTrack === 'flow' ? alphaRef : musicRef;
    const otherRef = selectedTrack === 'flow' ? musicRef : alphaRef;

    if (!currentRef.current) return;

    if (musicPlaying && selectedTrack === musicTrack) {
      currentRef.current.pause();
      setMusicPlaying(false);
    } else {
      if (otherRef.current) {
        otherRef.current.pause();
      }
      setMusicTrack(selectedTrack);
      currentRef.current.play()
        .then(() => setMusicPlaying(true))
        .catch((err) => console.error('Failed to play music:', err));
    }
  };

  const handlePlayNarration = async () => {
    try {
      // If already playing, pause it
      if (narrationPlaying && narrationRef.current) {
        narrationRef.current.pause();
        setNarrationPlaying(false);
        return;
      }

      // Track narration play event
      const token = localStorage.getItem('token');
      try {
        await axios.post(`${BACKEND_URL}/api/analytics/track`, {
          event_type: 'narration_play'
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* non-critical */ }

      // Check if we have a valid, non-empty src that can be played
      if (narrationRef.current && narrationRef.current.src && narrationRef.current.src !== window.location.href) {
        console.log('▶️ Attempting to play existing audio from src:', narrationRef.current.src);
        try {
          await narrationRef.current.play();
          console.log('✅ Existing audio playing successfully');
          setNarrationPlaying(true);
          return;
        } catch (playErr) {
          console.error('❌ Failed to play existing audio, will fetch new:', playErr);
          // Clear and continue to fetch new audio
          narrationRef.current.src = '';
        }
      }

      if (!interpretation) {
        console.error('❌ No interpretation available');
        alert('Interpretation not available');
        return;
      }

      console.log('🔄 Starting narration fetch from backend');
      setNarrationLoading(true);
      setNarrationLoadingMessage('Preparing audio...');
      
      // Show extended message after 5 seconds (first-time generation takes longer)
      const loadingTimer = setTimeout(() => {
        setNarrationLoadingMessage('Generating narration (first time may take 30-60 seconds)...');
      }, 5000);

      // Always fetch from backend (which has DB cache) for simplicity and reliability
      const requestData = {
        interpretation_data: {
          anchor: interpretation.anchor || '',
          narration: interpretation.narration || interpretation.interpretation_text || '',
          insights: interpretation.insights || [],
          priorities: interpretation.priorities || [],
          journal_entries: interpretation.journal_entries || []
        },
        journey_index: selectedJourneyIndex >= 0 ? selectedJourneyIndex : null
      };
      
      console.log('📤 Fetching narration for journey_index:', requestData.journey_index);

      const response = await axios.post(
        `${BACKEND_URL}/api/interpretation/play-narration`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000,
          validateStatus: (status) => status === 200 || status === 202
        }
      );
      
      // Handle 202 - narration is being generated in background
      if (response.status === 202) {
        console.log('Narration is being generated, polling for completion...');
        setNarrationLoadingMessage('Your narration is being prepared (this may take up to 60 seconds)...');
        
        // Poll for narration readiness
        const pollInterval = 5000; // 5 seconds
        const maxPolls = 30; // 2.5 minutes max
        let pollCount = 0;
        
        const pollForAudio = async () => {
          while (pollCount < maxPolls) {
            pollCount++;
            await new Promise(resolve => setTimeout(resolve, pollInterval));
            
            try {
              const statusRes = await axios.get(
                `${BACKEND_URL}/api/interpretation/narration-status`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              if (statusRes.data.status === 'ready') {
                console.log('Narration ready! Fetching audio...');
                setNarrationLoadingMessage('Audio ready! Loading...');
                
                // Fetch the cached audio
                const audioRes = await axios.post(
                  `${BACKEND_URL}/api/interpretation/play-narration`,
                  requestData,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                    timeout: 30000
                  }
                );
                
                const audioBlob = audioRes.data;
                if (!audioBlob || audioBlob.size === 0) {
                  throw new Error('Received empty audio response');
                }
                
                const audioUrl = URL.createObjectURL(audioBlob);
                if (narrationRef.current) {
                  if (narrationRef.current.src && narrationRef.current.src.startsWith('blob:')) {
                    URL.revokeObjectURL(narrationRef.current.src);
                  }
                  narrationRef.current.src = audioUrl;
                  try {
                    await narrationRef.current.play();
                    setNarrationPlaying(true);
                  } catch (playError) {
                    if (playError.name === 'NotAllowedError') {
                      alert('Audio is ready! Please tap the Play button again to start playback.');
                    }
                  }
                }
                clearTimeout(loadingTimer);
                setNarrationLoading(false);
                return;
              } else if (statusRes.data.status === 'generating') {
                const remaining = Math.max(0, 60 - (pollCount * 5));
                setNarrationLoadingMessage(`Preparing your narration (~${remaining}s remaining)...`);
              } else {
                // Not started or error - trigger generation again
                break;
              }
            } catch (pollErr) {
              console.error('Poll error:', pollErr);
            }
          }
          
          // Polling timed out
          clearTimeout(loadingTimer);
          setNarrationLoading(false);
          alert('Narration generation is taking longer than expected. Please try again in a moment.');
        };
        
        await pollForAudio();
        return;
      }
      
      console.log('Response received, blob size:', response.data.size);

      // Create URL for the audio blob and play
      const audioBlob = response.data;
      
      // Check if we got a valid audio blob
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('Received empty audio response');
      }
      
      console.log('📦 Audio blob received, size:', audioBlob.size, 'type:', audioBlob.type);
      
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (narrationRef.current) {
        // Clear any old object URLs to prevent memory leaks
        if (narrationRef.current.src && narrationRef.current.src.startsWith('blob:')) {
          URL.revokeObjectURL(narrationRef.current.src);
        }
        
        narrationRef.current.src = audioUrl;
        
        // Mobile browsers may block autoplay - handle gracefully
        try {
          await narrationRef.current.play();
          console.log('✅ Audio playing successfully');
          setNarrationPlaying(true);
        } catch (playError) {
          console.error('⚠️ Autoplay blocked, trying with user gesture:', playError);
          // For mobile: the audio is loaded, user can tap play button again
          // Set playing to false but keep the audio loaded
          setNarrationPlaying(false);
          setNarrationLoading(false);
          clearTimeout(loadingTimer);
          
          // Show a more helpful message for mobile users
          if (playError.name === 'NotAllowedError') {
            alert('Audio is ready! Please tap the Play button again to start playback.');
            return;
          }
          throw playError;
        }
      }

      clearTimeout(loadingTimer);
      setNarrationLoading(false);
    } catch (err) {
      console.error('❌ Error loading narration:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to load narration.';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'The narration is taking longer than expected. Please try "Download Audio" first, then play it.';
      } else if (err.response?.status === 503) {
        errorMessage = 'Narration service is temporarily unavailable. Please try again in a moment.';
      }
      
      alert(errorMessage);
      setNarrationLoading(false);
      setNarrationPlaying(false);
    }
  };


  const handleDownloadPDF = async () => {
    try {
      if (!interpretation) {
        alert('Interpretation not available');
        return;
      }

      setPdfDownloading(true);
      const token = localStorage.getItem('token');
      
      // Track PDF download
      try {
        await axios.post(`${BACKEND_URL}/api/analytics/track`, {
          event_type: 'pdf_download'
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* non-critical */ }
      
      // Send the already-generated interpretation data (with anchor and narration)
      const requestData = {
        interpretation_data: {
          anchor: interpretation.anchor || '',
          narration: interpretation.narration || interpretation.interpretation_text || '',
          insights: interpretation.insights || [],
          priorities: interpretation.priorities || [],
          journal_entries: interpretation.journal_entries || []
        }
      };
      
      // Make API call to get PDF using existing interpretation
      const response = await axios.post(
        `${BACKEND_URL}/api/interpretation/download-pdf`,
        requestData,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clarity_reflection_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleDownloadAudio = async () => {
    try {
      if (!interpretation) {
        alert('Interpretation not available');
        return;
      }

      const token = localStorage.getItem('token');
      
      // Track audio download
      try {
        await axios.post(`${BACKEND_URL}/api/analytics/track`, {
          event_type: 'audio_download'
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* non-critical */ }
      
      // Show non-blocking loading state
      setAudioDownloading(true);
      setAudioDownloadMessage('Preparing audio...');
      
      // Update message after 5 seconds for first-time generation
      const loadingTimer = setTimeout(() => {
        setAudioDownloadMessage('Generating audio (first time may take 30-60 seconds)...');
      }, 5000);
      
      const requestData = {
        interpretation_data: {
          anchor: interpretation.anchor || '',
          narration: interpretation.narration || interpretation.interpretation_text || '',
          insights: interpretation.insights || [],
          priorities: interpretation.priorities || [],
          journal_entries: interpretation.journal_entries || []
        },
        journey_index: selectedJourneyIndex >= 0 ? selectedJourneyIndex : null
      };
      
      console.log('📥 Downloading audio with interpretation data...');
      
      // Call the download-audio endpoint which handles caching and mixing
      const response = await axios.post(
        `${BACKEND_URL}/api/interpretation/download-audio`,
        requestData,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000,
          validateStatus: (status) => status === 200 || status === 202
        }
      );

      // Handle 202 - audio is being generated in background
      if (response.status === 202) {
        setAudioDownloadMessage('Audio is being prepared. Polling for completion...');
        
        const pollInterval = 5000;
        const maxPolls = 30;
        let pollCount = 0;
        
        while (pollCount < maxPolls) {
          pollCount++;
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          
          try {
            const statusRes = await axios.get(
              `${BACKEND_URL}/api/interpretation/narration-status`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (statusRes.data.status === 'ready') {
              setAudioDownloadMessage('Audio ready! Downloading...');
              // Retry the download
              const retryRes = await axios.post(
                `${BACKEND_URL}/api/interpretation/download-audio`,
                requestData,
                { 
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: 'blob',
                  timeout: 60000
                }
              );
              
              if (retryRes.data && retryRes.data.size > 0) {
                const blob = new Blob([retryRes.data], { type: 'audio/mpeg' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `clarity_interpretation_${new Date().toISOString().split('T')[0]}.mp3`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
              }
              
              clearTimeout(loadingTimer);
              setAudioDownloading(false);
              setAudioDownloadMessage('');
              return;
            } else {
              const remaining = Math.max(0, 60 - (pollCount * 5));
              setAudioDownloadMessage(`Preparing audio (~${remaining}s remaining)...`);
            }
          } catch (pollErr) {
            console.error('Poll error:', pollErr);
          }
        }
        
        clearTimeout(loadingTimer);
        setAudioDownloading(false);
        setAudioDownloadMessage('');
        alert('Audio generation is taking longer than expected. Please try again in a moment.');
        return;
      }

      clearTimeout(loadingTimer);

      // Check if we got a valid response
      if (!response.data || response.data.size === 0) {
        throw new Error('Received empty audio response');
      }
      
      console.log('✅ Audio received, size:', response.data.size, 'bytes');

      // Create download link
      const blob = new Blob([response.data], { type: 'audio/mpeg' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clarity_interpretation_${new Date().toISOString().split('T')[0]}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setAudioDownloading(false);
      setAudioDownloadMessage('');
      // Success - no alert needed, file is downloading
      console.log('✅ Audio download triggered successfully');
    } catch (err) {
      console.error('Error downloading audio:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data
      });
      
      setAudioDownloading(false);
      setAudioDownloadMessage('');
      
      // Provide more specific error messages
      let errorMessage = 'Failed to download audio.';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Audio generation timed out. Please try again.';
      } else if (err.response?.status === 503) {
        errorMessage = 'Audio service is temporarily unavailable. Please try again later.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error generating audio. Please try again.';
      }
      
      alert(errorMessage);
    }
  };

  const handleDownloadBackgroundMusic = () => {
    try {
      // Create link to download background music
      const link = document.createElement('a');
      link.href = '/assets/page2/background-music.mp3';
      link.setAttribute('download', `clarity_background_music.mp3`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Error downloading background music:', err);
      alert('Failed to download background music. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--grey-deep)' }}>
        <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
        <div className="landing-orb cool focus-shift" style={{ '--orb-opacity': '0.4', top: '20%', left: '10%', width: '40vw', height: '40vw', maxWidth: '400px', maxHeight: '400px' }} />
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--accent-teal)' }}></div>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '1.3rem', color: 'var(--text-primary)' }}>Generating your interpretation...</div>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.88rem', color: 'var(--grey-mid)', marginTop: '8px' }}>This may take a moment</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--grey-deep)' }}>
        <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
        <div className="text-center relative z-10 px-6" style={{ maxWidth: '400px' }}>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '1.1rem', color: '#fca5a5', marginBottom: '24px', lineHeight: 1.6 }}>{error}</div>
          <div className="flex flex-col gap-4 items-center">
            {retryResponses && (
              <button
                onClick={() => { setError(''); setLoading(true); generateInterpretation(retryResponses); }}
                className="cta-pill-landing"
              >
                Retry
              </button>
            )}
            <button
              onClick={() => navigate('/journaling')}
              className="cta-pill-landing"
              style={{ opacity: 0.5 }}
            >
              Back to Journaling
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-h-screen flex flex-col relative overflow-hidden" style={{ background: 'transparent' }}>
      {/* Blurred Background Image */}
      <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
      
      {/* Gradient Orbs */}
      <div className="landing-orb cool focus-shift" style={{ '--orb-opacity': '0.85', top: '0%', left: '-10%', width: '55vw', height: '55vw', maxWidth: '650px', maxHeight: '650px', position: 'fixed', zIndex: 0 }} />
      <div className="landing-orb neutral" style={{ '--orb-opacity': '0.75', top: '35%', right: '-10%', width: '50vw', height: '50vw', maxWidth: '600px', maxHeight: '600px', animationDelay: '-5s', position: 'fixed', zIndex: 0 }} />
      <div className="landing-orb cool" style={{ '--orb-opacity': '0.65', top: '65%', left: '0%', width: '48vw', height: '48vw', maxWidth: '550px', maxHeight: '550px', animationDelay: '-10s', position: 'fixed', zIndex: 0 }} />

      {/* Beach Music */}
      <audio
        ref={musicRef}
        src="/assets/page2/background-music.mp3"
        loop
        preload="metadata"
      />
      {/* Flow Music */}
      <audio
        ref={alphaRef}
        src="/assets/interpretation/alpha-focus-state.mp3"
        loop
        preload="metadata"
      />

      {/* Narration Audio - loops continuously until paused */}
      <audio
        ref={narrationRef}
        preload="none"
        loop
        onPause={() => setNarrationPlaying(false)}
        onPlay={() => setNarrationPlaying(true)}
      />

      {/* Audio Controls and Logout - Top */}
      <div className="fixed top-0 left-0 right-0 z-50 flex flex-col gap-2 items-center" style={{ padding: '12px 16px', background: 'rgba(26,26,26,0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
        {/* Audio controls row */}
        <div className="flex flex-row gap-2 justify-center flex-wrap">
          <button
            onClick={handlePlayNarration}
            disabled={narrationLoading}
            className="cta-pill-landing text-center disabled:opacity-50"
            style={{ minWidth: '140px', fontSize: '0.75rem', padding: '7px 12px' }}
            data-testid="play-narration-btn"
          >
            {narrationLoading ? narrationLoadingMessage : (narrationPlaying ? 'Pause Narration' : 'Play Narration')}
          </button>
          <button
            onClick={() => handleMusicToggle('beach')}
            className="cta-pill-landing text-center"
            style={{ minWidth: '100px', fontSize: '0.75rem', padding: '7px 12px', opacity: musicPlaying && musicTrack === 'beach' ? 1 : 0.6 }}
            data-testid="toggle-beach-btn"
          >
            {musicPlaying && musicTrack === 'beach' ? 'Beach ◉' : 'Beach'}
          </button>
          <button
            onClick={() => handleMusicToggle('flow')}
            className="cta-pill-landing text-center"
            style={{ minWidth: '100px', fontSize: '0.75rem', padding: '7px 12px', opacity: musicPlaying && musicTrack === 'flow' ? 1 : 0.6 }}
            data-testid="toggle-flow-btn"
          >
            {musicPlaying && musicTrack === 'flow' ? 'Flow ◉' : 'Flow'}
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('token');
              navigate('/');
            }}
            className="cta-pill-landing text-center"
            style={{ minWidth: '80px', fontSize: '0.75rem', padding: '7px 12px' }}
            title="You can always return to view your interpretation"
          >
            Logout
          </button>
        </div>
      </div>

      {/* New Session Available Banner */}
      {hasUnusedAccess && (
        <div className="fixed top-20 sm:top-24 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-lg px-4">
          <div className="new-session-banner">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3>New Session Available!</h3>
                <p>You have a paid session ready. Start your new journaling journey.</p>
              </div>
              <button
                onClick={() => navigate('/welcome')}
                className="cta-pill whitespace-nowrap"
                style={{ padding: '10px 20px', fontSize: '0.9rem' }}
              >
                Start Now
              </button>
            </div>
            <p className="text-body mt-3" style={{ fontSize: '0.78rem', opacity: 0.7 }}>
              Your previous interpretation is shown below for reference.
            </p>
          </div>
        </div>
      )}

      {/* Narration Loading Indicator */}
      {narrationLoading && (
        <div className="fixed top-16 left-0 right-0 z-40 flex justify-center" style={{ pointerEvents: 'none' }}>
          <div style={{
            background: 'rgba(26, 26, 26, 0.85)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            borderRadius: '24px',
            padding: '8px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            border: '1px solid rgba(184, 212, 208, 0.15)'
          }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-teal)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <span style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              {narrationLoadingMessage || 'Preparing your narration...'}
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-start overflow-y-auto">
        <div className="w-full max-w-2xl space-y-6 px-6 sm:px-8 md:px-12" style={{ 
          paddingTop: '80px', 
          paddingBottom: '32px' 
        }}>
          
          {/* Section Label */}
          <p className="insights-label text-center">Your Clarity Map</p>

          {/* Title */}
          <div className="text-center" style={{ marginTop: '16px' }}>
            <h1 className="text-display" style={{ fontSize: 'clamp(1.2rem, 3vw, 1.55rem)' }}>
              Insights From Your Reflection
            </h1>
            {previousInterpretations.length > 0 && (
              <div className="flex flex-wrap gap-4 items-center justify-center mt-4">
                <button
                  onClick={() => setShowPastJourneys(!showPastJourneys)}
                  className="ghost-link"
                  style={{ fontSize: '0.9rem' }}
                >
                  {showPastJourneys ? '✕ Close' : `View Past Journeys (${previousInterpretations.length})`}
                </button>
                {selectedJourneyIndex >= 0 && (
                  <button
                    onClick={async () => {
                      if (narrationRef.current) {
                        narrationRef.current.pause();
                        if (narrationRef.current.src && narrationRef.current.src.startsWith('blob:')) {
                          URL.revokeObjectURL(narrationRef.current.src);
                        }
                        narrationRef.current.src = '';
                        narrationRef.current.load();
                      }
                      setNarrationPlaying(false);
                      await loadExistingInterpretation();
                      setSelectedJourneyIndex(-1);
                      setShowPastJourneys(false);
                    }}
                    className="ghost-link"
                    style={{ fontSize: '0.9rem', color: 'var(--accent-sand)' }}
                  >
                    ← Back to Current Journey
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Tide Line */}
          <div className="tide-line" style={{ margin: '32px 0' }} />

          {/* Past Journeys Selection */}
          {showPastJourneys && previousInterpretations.length > 0 && (
            <div className="space-y-3">
              <p className="insights-label">Your Journaling History</p>
              {previousInterpretations.map((journey, idx) => {
                try {
                  const data = journey?.interpretation_data;
                  if (!data) return null;
                  
                  const anchorPreview = (data.anchor || 'No preview available').substring(0, 100);
                  const completedDate = journey.completed_at ? new Date(journey.completed_at).toLocaleDateString() : 'Unknown date';
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        try {
                          if (narrationRef.current) {
                            narrationRef.current.pause();
                            if (narrationRef.current.src && narrationRef.current.src.startsWith('blob:')) {
                              URL.revokeObjectURL(narrationRef.current.src);
                            }
                            narrationRef.current.src = '';
                            narrationRef.current.load();
                          }
                          setNarrationPlaying(false);
                          setInterpretation({
                            anchor: data.anchor || 'No anchor available',
                            interpretation_text: data.interpretation || 'No interpretation available',
                            priorities: data.priorities || [],
                            full_text_length: (data.interpretation || '').length
                          });
                          setSelectedJourneyIndex(idx);
                          setShowPastJourneys(false);
                        } catch (err) {
                          console.error('Error loading journey:', err);
                        }
                      }}
                      className="past-journey-item w-full text-left"
                    >
                      <div className="text-display" style={{ fontSize: '1.1rem' }}>Journey #{previousInterpretations.length - idx}</div>
                      <div className="text-body mt-1" style={{ fontSize: '0.85rem' }}>
                        Completed: {completedDate}
                      </div>
                      <div className="text-body mt-2" style={{ fontSize: '0.82rem', opacity: 0.6 }}>
                        {anchorPreview}...
                      </div>
                    </button>
                  );
                } catch (err) {
                  return null;
                }
              })}
            </div>
          )}

          {/* Narration Text - This is Part 1, the only text shown on screen */}
          {interpretation?.insights && interpretation.insights.length > 0 && (
            <div className="space-y-8">
              {/* Anchor Section - displayed prominently at the top */}
              {interpretation.anchor && (
                <div className="anchor-section reveal">
                  {interpretation.anchor}
                </div>
              )}
              
              <div className="tide-line" style={{ margin: '24px 0' }} />
              
              {/* Main Reflection - Combined body from insight Parts 1,2,4 */}
              <div>
                <p className="insights-label">Your Reflection</p>
                <div className="reflection-body">
                  <p className="reveal">
                    {[0, 1, 3]
                      .filter(idx => idx < interpretation.insights.length)
                      .map(idx => interpretation.insights[idx]?.insight || '')
                      .filter(text => text)
                      .join(' ')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* === FOLD/UNFOLD SECTIONS (stacked directly after narration) === */}
          <div className="space-y-4 mt-12">
            
            {/* 1. Artifacts for Downloads - Fold/Unfold */}
            <div>
              <button
                onClick={() => setShowArtifacts(!showArtifacts)}
                className="fold-header w-full"
              >
                <span className="fold-title">Artifacts for Downloads</span>
                <span className={`fold-icon ${showArtifacts ? 'open' : ''}`}>↓</span>
              </button>
              {showArtifacts && (
                <div className="fold-content">
                  <div 
                    onClick={handleDownloadPDF}
                    className={`download-row ${pdfDownloading ? 'opacity-50 cursor-wait' : ''}`}
                    style={{ cursor: pdfDownloading ? 'wait' : 'pointer' }}
                  >
                    <div>
                      <span className="download-text">{pdfDownloading ? 'Preparing...' : 'Your Clarity Map'}</span>
                      <span className="download-label">PDF</span>
                    </div>
                    <span className="download-arrow">→</span>
                  </div>
                  
                  <div 
                    onClick={handleDownloadAudio}
                    className={`download-row ${audioDownloading ? 'opacity-50 cursor-wait' : ''}`}
                    style={{ cursor: audioDownloading ? 'wait' : 'pointer' }}
                  >
                    <div>
                      <span className="download-text">{audioDownloading ? audioDownloadMessage : 'Audio Interpretation'}</span>
                      <span className="download-label">AUDIO</span>
                    </div>
                    <span className="download-arrow">→</span>
                  </div>
                  
                  <div 
                    onClick={handleDownloadBackgroundMusic}
                    className="download-row"
                  >
                    <div>
                      <span className="download-text">Background Music</span>
                      <span className="download-label">MUSIC</span>
                    </div>
                    <span className="download-arrow">→</span>
                  </div>
                    
                    {/* Full Exit Consent Section */}
                    {showConsentSection && (
                      <div className="landing-form-card mt-8">
                        <h3 style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '16px' }}>
                          Consent &amp; Preferences
                        </h3>
                        <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.85rem', color: 'var(--grey-mid)', lineHeight: 1.7, marginBottom: '24px' }}>
                          We'd like to ask for your consent on how your journaling insights will be handled.
                        </p>

                        {/* Insights & Artifacts Section */}
                        <div style={{ marginBottom: '24px' }}>
                          <h4 style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.82rem', letterSpacing: '0.04em', color: 'var(--accent-teal)', marginBottom: '12px' }}>
                            Insights &amp; Artifacts
                          </h4>
                          <div className="space-y-3">
                            <label className="flex items-start space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={dataRetention === 'keep'}
                                onChange={() => setDataRetention(dataRetention === 'keep' ? null : 'keep')}
                                className="w-5 h-5 mt-1 flex-shrink-0 rounded"
                                style={{ accentColor: 'var(--accent-teal)' }}
                              />
                              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.85rem', color: 'var(--grey-light)', lineHeight: 1.6 }}>
                                <strong style={{ fontWeight: 400 }}>Keep your insights for future access</strong>: Your insights will remain available in the app for you to revisit anytime.
                              </span>
                            </label>
                            <label className="flex items-start space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={dataRetention === 'delete'}
                                onChange={() => setDataRetention(dataRetention === 'delete' ? null : 'delete')}
                                className="w-5 h-5 mt-1 flex-shrink-0 rounded"
                                style={{ accentColor: 'var(--accent-teal)' }}
                              />
                              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.85rem', color: 'var(--grey-light)', lineHeight: 1.6 }}>
                                <strong style={{ fontWeight: 400 }}>Delete after 24 Hours.</strong>
                              </span>
                            </label>
                            <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.78rem', color: 'rgba(163, 163, 163, 0.5)', marginLeft: '32px' }}>
                              Please note: once deleted, insights cannot be retrieved or restored.
                            </p>
                          </div>
                        </div>

                        {/* Thank You Message */}
                        <div style={{ marginBottom: '24px', padding: '16px', borderRadius: '12px', background: 'rgba(36, 44, 53, 0.3)', textAlign: 'center' }}>
                          <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                            Thank you again for deciding to have this mini journaling session being a part of your journey. See you in our next project.
                          </p>
                        </div>

                        {/* Save Preferences Button */}
                        <div className="flex justify-center">
                          <button
                            onClick={handleSaveConsent}
                            disabled={(dataRetention === null) || savingPreferences}
                            className="cta-pill-landing"
                            style={{ padding: '12px 32px', fontSize: '0.82rem' }}
                          >
                            {savingPreferences ? 'Saving...' : 'Save My Preferences'}
                          </button>
                        </div>
                        {dataRetention === null && (
                          <p className="text-center" style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.75rem', color: 'rgba(163, 163, 163, 0.4)', marginTop: '12px' }}>
                            Please select at least one preference above to save.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
        </div>
      </div>
      <div style={{ position: 'relative', zIndex: 10, background: 'var(--grey-deep)' }}>
        <LegalFooter />
      </div>
    </div>
  );
};

export default InterpretationPage;
